import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import apiClient from '../api';
import { useAuth } from '../AuthContext';

// ====================================
// GUIDED TOUR CONTEXT
// ====================================

const GuidedTourContext = createContext(null);

export const useGuidedTour = () => {
  const context = useContext(GuidedTourContext);
  if (!context) {
    throw new Error('useGuidedTour must be used within GuidedTourProvider');
  }
  return context;
};

// ====================================
// TOUR STYLES
// ====================================

const tourStyles = {
  options: {
    arrowColor: '#fff',
    backgroundColor: '#fff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    primaryColor: '#2563eb',
    textColor: '#1e293b',
    zIndex: 10000,
    beaconSize: 36,
    spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
  },
  tooltipContainer: {
    textAlign: 'left',
    maxWidth: 380,
  },
  buttonNext: {
    backgroundColor: '#2563eb',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    padding: '10px 20px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonBack: {
    color: '#64748b',
    marginRight: 10,
    fontSize: '14px',
    padding: '10px 16px',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
  },
  buttonSkip: {
    color: '#94a3b8',
    fontSize: '13px',
    padding: '10px 16px',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
  },
  buttonClose: {
    display: 'none',
  },
  tooltip: {
    borderRadius: '12px',
    padding: 0,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  tooltipContent: {
    padding: '20px 24px',
  },
  tooltipTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#0f172a',
  },
  tooltipFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  spotlight: {
    borderRadius: '8px',
  },
};

// ====================================
// CUSTOM TOOLTIP COMPONENT
// ====================================

const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  _closeProps, // R10e35b-8.5-006: Unused but received from Joyride
  primaryProps,
  skipProps,
  tooltipProps,
  size,
}) => {
  return (
    <div {...tooltipProps} className="bg-white rounded-xl shadow-2xl max-w-[380px] overflow-hidden">
      {/* Header with progress */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-blue-100 text-xs font-medium uppercase tracking-wider">
            Step {index + 1} of {size}
          </span>
          <button
            {...skipProps}
            className="text-blue-200 hover:text-white text-xs transition-colors"
          >
            Skip tour
          </button>
        </div>
        <h3 className="text-white text-lg font-semibold">{step.title}</h3>
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        <p className="text-slate-600 text-sm leading-relaxed">{step.content}</p>
      </div>

      {/* Progress bar */}
      <div className="px-6 pb-2">
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${((index + 1) / size) * 100}%` }}
          />
        </div>
        <p className="text-xs text-slate-400 mt-1 text-center">Step {index + 1} of {size}</p>
      </div>

      {/* Footer with buttons */}
      <div className="px-6 py-4 bg-slate-50 flex items-center justify-between border-t">
        <div className="flex items-center gap-2">
          {/* R10e35b-8.5-003: Skip Tour escape hatch */}
          <button
            {...skipProps}
            className="text-slate-400 hover:text-slate-600 text-xs font-medium transition-colors underline"
            data-testid="skip-tour-button"
          >
            Skip tour
          </button>
          {index > 0 && (
            <span className="text-slate-300 mx-1">|</span>
          )}
          {index > 0 && (
            <button
              {...backProps}
              className="text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
            >
              Back
            </button>
          )}
        </div>
        <button
          {...primaryProps}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {continuous ? (index === size - 1 ? 'Finish' : 'Next') : 'Got it'}
        </button>
      </div>
    </div>
  );
};

// ====================================
// PROVIDER COMPONENT
// ====================================

export const GuidedTourProvider = ({ children }) => {
  const { user } = useAuth();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentTour, setCurrentTour] = useState(null);
  const [completedTours, setCompletedTours] = useState({});
  const [stepIndex, setStepIndex] = useState(0);

  // Load completed tours from API
  useEffect(() => {
    if (user?.id) {
      loadCompletedTours();
    }
  }, [user?.id]);

  const loadCompletedTours = async () => {
    try {
      const res = await apiClient.get(`/wizards/status?user_id=${user.id}`);
      const wizards = res.data.wizards || {};
      setCompletedTours(wizards);
    } catch (e) {
      console.error('Failed to load tour status:', e);
    }
  };

  // Start a guided tour
  const startTour = useCallback(async (tourType) => {
    if (completedTours[tourType]?.completed) {
      console.log(`Tour ${tourType} already completed`);
      return;
    }

    try {
      const res = await apiClient.get(`/wizards/tours/${tourType}`);
      const tourSteps = res.data.steps || [];
      
      if (tourSteps.length === 0) {
        console.warn(`No steps found for tour: ${tourType}`);
        return;
      }

      setSteps(tourSteps);
      setCurrentTour(tourType);
      setStepIndex(0);
      setRun(true);
    } catch (e) {
      console.error('Failed to start tour:', e);
    }
  }, [completedTours]);

  // Force restart a tour (even if completed)
  const restartTour = useCallback(async (tourType) => {
    try {
      await apiClient.post(`/wizards/reset/${tourType}?user_id=${user?.id || 'current'}`);
      setCompletedTours(prev => {
        const updated = { ...prev };
        delete updated[tourType];
        return updated;
      });
      await startTour(tourType);
    } catch (e) {
      console.error('Failed to restart tour:', e);
    }
  }, [user?.id, startTour]);

  // Stop current tour
  const stopTour = useCallback(() => {
    setRun(false);
    setSteps([]);
    setCurrentTour(null);
    setStepIndex(0);
  }, []);

  // Handle Joyride callbacks
  const handleJoyrideCallback = useCallback(async (data) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Mark tour as completed
      const skipped = status === STATUS.SKIPPED;
      
      try {
        await apiClient.post(`/wizards/tours/${currentTour}/complete`, null, {
          params: { user_id: user?.id || 'current', skipped }
        });
        
        setCompletedTours(prev => ({
          ...prev,
          [currentTour]: { completed: true, skipped }
        }));
      } catch (e) {
        console.error('Failed to mark tour complete:', e);
      }

      stopTour();
    }
  }, [currentTour, user?.id, stopTour]);

  // Check if a tour is completed
  const isTourCompleted = useCallback((tourType) => {
    return completedTours[tourType]?.completed || false;
  }, [completedTours]);

  // Get tour status
  const getTourStatus = useCallback((tourType) => {
    return completedTours[tourType] || { completed: false, skipped: false };
  }, [completedTours]);

  const value = {
    run,
    steps,
    currentTour,
    completedTours,
    startTour,
    restartTour,
    stopTour,
    isTourCompleted,
    getTourStatus,
  };

  return (
    <GuidedTourContext.Provider value={value}>
      {children}
      <Joyride
        run={run}
        steps={steps}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        disableOverlayClose
        spotlightClicks
        callback={handleJoyrideCallback}
        styles={tourStyles}
        tooltipComponent={CustomTooltip}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip tour',
        }}
      />
    </GuidedTourContext.Provider>
  );
};

// ====================================
// HELPER HOOK: AUTO-START TOUR
// ====================================

export const useAutoStartTour = (tourType, delay = 1000) => {
  const { startTour, isTourCompleted } = useGuidedTour();

  useEffect(() => {
    if (!isTourCompleted(tourType)) {
      const timer = setTimeout(() => {
        startTour(tourType);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [tourType, delay, startTour, isTourCompleted]);
};

// ====================================
// TOUR TRIGGER BUTTON COMPONENT
// ====================================

export const TourTriggerButton = ({ tourType, children, className = '' }) => {
  const { startTour, restartTour, isTourCompleted } = useGuidedTour();
  const completed = isTourCompleted(tourType);

  const handleClick = () => {
    if (completed) {
      restartTour(tourType);
    } else {
      startTour(tourType);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium ${className}`}
      data-testid={`tour-trigger-${tourType}`}
    >
      {children || (completed ? 'Replay Tour' : 'Start Tour')}
    </button>
  );
};

export default GuidedTourProvider;
